import { Bell, Calendar, Repeat } from 'lucide-react';
import type { ReactNode } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import type { TaskStatusMeta, WorkspaceMember } from '../../lib/task-list';
import { TASK_PRIORITY_OPTIONS } from '../../lib/task-list';
import { REMIND_BEFORE_OPTIONS, REPEAT_OPTIONS, type TaskFormValues } from '../../types/task-form';
import { Input } from '../../ui/input';
import { Label } from '../../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../ui/select';
import { Switch } from '../../ui/switch';
import { TiptapEditor } from '../comments/tiptap-editor';
import { ContactPicker } from '../contacts/ContactPicker';
import { ControlledDatePicker } from '../fields/ControlledDatePicker';
import { ControlledTimePicker } from '../fields/ControlledTimePicker';

export interface TaskFormFieldsProps {
  /** Workspace task statuses — the valid set is data, not a compile-time enum. */
  statuses: TaskStatusMeta[];
  members: WorkspaceMember[];
  /** Mention targets for the description editor. */
  users?: WorkspaceMember[];
  /** Forces a due date, for surfaces that only make sense with one (the calendar). */
  requireDueDate?: boolean;
  /** Hides the linked-contact picker where a task's parent is fixed by the surface. */
  showContactPicker?: boolean;
  /** Extra content after the standard fields — an invite-by-email row, say. */
  footerSlot?: ReactNode;
  disabled?: boolean;
}

/**
 * The one set of fields every task authoring surface renders — the portals' create dialog and
 * side panel, and the extension's create/edit route.
 *
 * Presentational only: it reads and writes a `react-hook-form` context and owns no submission,
 * routing, or mutation. Each surface keeps its own chrome and its own submit, which is where they
 * genuinely differ; sharing those too would drag routing into shared code.
 *
 * This exists because the portals and the extension had drifted into two arbitrary halves of one
 * form — the portals could link a contact but never set a reminder, the extension the reverse —
 * so a task's capabilities depended on which window created it.
 */
export function TaskFormFields({
  statuses,
  members,
  users,
  requireDueDate = false,
  showContactPicker = true,
  footerSlot,
  disabled = false,
}: TaskFormFieldsProps) {
  const { control, watch } = useFormContext<TaskFormValues>();

  const dueEnabled = watch('dueEnabled');
  const reminderEnabled = watch('reminderEnabled');
  const repeatFrequency = watch('repeatFrequency');
  const endRepeatEnabled = watch('endRepeatEnabled');

  // A relative reminder and a repeat both anchor to the due date, so neither is offered without
  // one. The portals previously sidestepped this by omitting the fields entirely.
  const dueDateActive = requireDueDate || dueEnabled;

  return (
    <div className="space-y-4">
      <div className="space-y-2">
        <Label htmlFor="task-title">Title</Label>
        <Controller
          name="title"
          control={control}
          render={({ field, fieldState }) => (
            <>
              <Input
                {...field}
                id="task-title"
                placeholder="Task title"
                disabled={disabled}
                autoFocus
              />
              {fieldState.error && (
                <p className="text-xs text-destructive">{fieldState.error.message}</p>
              )}
            </>
          )}
        />
      </div>

      <div className="space-y-2">
        <Label htmlFor="task-description">Description</Label>
        <Controller
          name="description"
          control={control}
          render={({ field }) => (
            <TiptapEditor
              value={field.value}
              onChange={field.onChange}
              placeholder="Add description..."
              autoFocus={false}
              users={users ?? members}
              minRows={3}
            />
          )}
        />
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label htmlFor="task-status">Status</Label>
          <Controller
            name="status"
            control={control}
            render={({ field }) => (
              <Select value={field.value} onValueChange={field.onChange} disabled={disabled}>
                <SelectTrigger id="task-status">
                  <SelectValue placeholder="Select status" />
                </SelectTrigger>
                <SelectContent>
                  {statuses.map((status) => (
                    <SelectItem key={status.key} value={status.key}>
                      {status.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="task-priority">Priority</Label>
          <Controller
            name="priority"
            control={control}
            render={({ field }) => (
              <Select
                value={String(field.value)}
                onValueChange={(value) => field.onChange(Number(value))}
                disabled={disabled}
              >
                <SelectTrigger id="task-priority">
                  <SelectValue placeholder="Select priority" />
                </SelectTrigger>
                <SelectContent>
                  {TASK_PRIORITY_OPTIONS.map((option) => (
                    <SelectItem key={option.value} value={String(option.value)}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          />
        </div>
      </div>

      <div className="space-y-2">
        <Label htmlFor="task-assignee">Assignee</Label>
        <Controller
          name="assignedTo"
          control={control}
          render={({ field }) => (
            <Select
              value={field.value ? String(field.value) : 'unassigned'}
              onValueChange={(value) =>
                field.onChange(value === 'unassigned' ? null : Number(value))
              }
              disabled={disabled}
            >
              <SelectTrigger id="task-assignee">
                <SelectValue placeholder="Unassigned" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="unassigned">Unassigned</SelectItem>
                {members.map((member) => (
                  <SelectItem key={member.id} value={String(member.id)}>
                    {member.fullName || member.email}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
        />
      </div>

      {showContactPicker && (
        <div className="space-y-2">
          <Label htmlFor="task-contact">
            Related Contact <span className="font-normal text-muted-foreground">(optional)</span>
          </Label>
          <Controller
            name="contactId"
            control={control}
            render={({ field }) => (
              <ContactPicker
                id="task-contact"
                value={field.value ?? null}
                onChange={field.onChange}
                disabled={disabled}
              />
            )}
          />
        </div>
      )}

      <div className="space-y-3 rounded-xl border border-border/60 p-3">
        <div className="flex items-center justify-between">
          <Label
            htmlFor="task-due-toggle"
            className="flex cursor-pointer items-center gap-2 font-normal"
          >
            <Calendar className="h-4 w-4 text-muted-foreground" />
            Due date
            {requireDueDate && <span className="text-destructive">*</span>}
          </Label>
          {!requireDueDate && (
            <Controller
              name="dueEnabled"
              control={control}
              render={({ field }) => (
                <Switch
                  id="task-due-toggle"
                  checked={field.value}
                  onCheckedChange={field.onChange}
                  disabled={disabled}
                />
              )}
            />
          )}
        </div>

        {dueDateActive && (
          <>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label className="text-xs">Date</Label>
                <ControlledDatePicker name="dueDate" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">Time</Label>
                <ControlledTimePicker name="dueTime" />
              </div>
            </div>

            <div className="flex items-center justify-between border-t border-border/60 pt-3">
              <Label
                htmlFor="task-reminder-toggle"
                className="flex cursor-pointer items-center gap-2 font-normal"
              >
                <Bell className="h-4 w-4 text-muted-foreground" />
                Remind me
              </Label>
              <Controller
                name="reminderEnabled"
                control={control}
                render={({ field }) => (
                  <Switch
                    id="task-reminder-toggle"
                    checked={field.value}
                    onCheckedChange={field.onChange}
                    disabled={disabled}
                  />
                )}
              />
            </div>

            {reminderEnabled && (
              <Controller
                name="remindBeforeMinutes"
                control={control}
                render={({ field }) => (
                  <Select
                    value={String(field.value)}
                    onValueChange={(value) => field.onChange(Number(value))}
                    disabled={disabled}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {REMIND_BEFORE_OPTIONS.map((option) => (
                        <SelectItem key={option.value} value={String(option.value)}>
                          {option.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              />
            )}

            <div className="flex items-center justify-between border-t border-border/60 pt-3">
              <Label htmlFor="task-repeat" className="flex items-center gap-2 font-normal text-sm">
                <Repeat className="h-4 w-4 text-muted-foreground" />
                Repeat
              </Label>
              <Controller
                name="repeatFrequency"
                control={control}
                render={({ field }) => (
                  <Select value={field.value} onValueChange={field.onChange} disabled={disabled}>
                    <SelectTrigger id="task-repeat" className="w-[140px]">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {REPEAT_OPTIONS.map((option) => (
                        <SelectItem key={option.value} value={option.value}>
                          {option.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              />
            </div>

            {repeatFrequency !== 'never' && (
              <div className="flex items-center justify-between">
                <Label
                  htmlFor="task-end-repeat"
                  className="cursor-pointer font-normal text-sm text-muted-foreground"
                >
                  End repeat on a date
                </Label>
                <Controller
                  name="endRepeatEnabled"
                  control={control}
                  render={({ field }) => (
                    <Switch
                      id="task-end-repeat"
                      checked={field.value}
                      onCheckedChange={field.onChange}
                      disabled={disabled}
                    />
                  )}
                />
              </div>
            )}

            {repeatFrequency !== 'never' && endRepeatEnabled && (
              <ControlledDatePicker name="endRepeatDate" placeholder="End date" />
            )}
          </>
        )}
      </div>

      {footerSlot}
    </div>
  );
}
